import React, { useEffect, useRef } from "react";
import { Box, IconButton, Divider, Typography } from "@mui/material";
import { Add, HambergerMenu } from "iconsax-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { slimScrollbarSx } from "../../../utils/scrollbarStyles";

// One row, draggable only when `reorderable` — the drag listeners live on the
// handle icon alone (not the whole row), so a plain click anywhere else still
// selects the reply exactly like before dnd-kit was added; no click-vs-drag
// ambiguity to work around.
const QuickReplyRow = ({ reply, index, isLast, isSelected, reorderable, onSelect }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: reply._id, disabled: !reorderable });

  return (
    <React.Fragment>
      <Box
        ref={reorderable ? setNodeRef : undefined}
        data-index={index}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSelect(reply)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: reorderable ? 1 : 2,
          py: 1.25,
          cursor: "pointer",
          bgcolor: isSelected ? "action.selected" : "transparent",
          "&:hover": { bgcolor: "action.hover" },
          transform: reorderable ? CSS.Transform.toString(transform) : undefined,
          transition: reorderable ? transition : undefined,
          position: "relative",
          zIndex: isDragging ? 1 : "auto",
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        {reorderable && (
          <Box
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              mt: 0.25,
              cursor: "grab",
              color: "text.secondary",
              touchAction: "none",
              "&:active": { cursor: "grabbing" },
            }}
          >
            <HambergerMenu size="16" variant="Linear" />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "primary.main", mb: 0.25 }}>
            /{reply.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {reply.message}
          </Typography>
        </Box>
      </Box>
      {!isLast && <Divider />}
    </React.Fragment>
  );
};

const QuickReplyAutocomplete = ({
  items = [],
  selectedIndex = 0,
  onSelect,
  onClose,
  onCreateNew,
  // Reordering only makes sense when browsing the full unfiltered list — once
  // the user's typed a search query after "/", dragging a filtered subset
  // around wouldn't map to a sensible position in the real list underneath.
  reorderable = false,
  onReorder,
}) => {
  const listRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0 && items.length > 0) {
      const row = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (row) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex, items]);

  if (items.length === 0) return null;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const oldIndex = items.findIndex((r) => r._id === active.id);
    const newIndex = items.findIndex((r) => r._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex).map((r) => r._id));
  };

  const rows = items.map((reply, index) => (
    <QuickReplyRow
      key={reply._id}
      reply={reply}
      index={index}
      isLast={index === items.length - 1}
      isSelected={index === selectedIndex}
      reorderable={reorderable}
      onSelect={onSelect}
    />
  ));

  return (
    <Box
      sx={{
        position: "absolute",
        // Matches the emoji picker's exact anchor recipe (bottom:"100%"+mb:1,
        // not a baked-in calc(100%+8px)) — Rahim, 2026-08-24: "see how it has
        // proper padding and gap from below... [match] the emoji popup one".
        bottom: "100%",
        mb: 1,
        left: 0,
        right: "auto",
        width: { xs: "100%", sm: 480 },
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: "16px",
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "0px 4px 12px rgba(0,0,0,0.4)"
            : "0px 4px 12px rgba(0,0,0,0.15)",
        maxHeight: 420,
        overflow: "hidden",
        zIndex: (t) => t.zIndex.modal,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 0.5,
          px: 2,
          py: 1.25,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>
          Quick Replies
        </Typography>
        {/* Was a Close (X) button — swapped for "+ new reply" per Rahim's ask
            (2026-08-24). Sits right next to the heading, not pushed to the far
            edge. Closing the popup still works via outside-click/Escape,
            handled by TextBox.jsx same as before. */}
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onCreateNew}
          aria-label="Create a new quick reply"
          sx={{
            color: "text.secondary",
            "&:hover": {
              bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
            },
          }}
        >
          <Add size="24" color="currentColor" variant="Linear" />
        </IconButton>
      </Box>
      <Divider />
      <Box
        ref={listRef}
        sx={{
          overflowY: "auto",
          ...slimScrollbarSx,
          // No visible track/rail behind the thumb — just the thumb itself.
          scrollbarColor: (t) => (t.palette.mode === "dark" ? "#404040 transparent" : "#bdbdbd transparent"),
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
          },
          "&::-webkit-scrollbar-track-piece": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: "transparent",
          },
        }}
      >
        {reorderable ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((r) => r._id)} strategy={verticalListSortingStrategy}>
              {rows}
            </SortableContext>
          </DndContext>
        ) : (
          rows
        )}
      </Box>
    </Box>
  );
};

export default QuickReplyAutocomplete;
